import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { decorateChartSeries } from '../../constants/chartSeriesMetrics';
import {
  formatChartTickValue,
  formatDurationSeconds,
} from '../../utils/chartFormatUtils';
import useAnalyticsChartPalette from '../../hooks/useAnalyticsChartPalette';

export default function PieChartSection({
  result,
  colors = [],
  valueFormat = 'number',
  height = 300,
  compact = false,
}) {
  const { colors: themeColors, axis } = useAnalyticsChartPalette(colors);
  const formatValue = (value) => (
    valueFormat === 'duration'
      ? formatDurationSeconds(value)
      : formatChartTickValue(value)
  );

  const pieData = useMemo(() => {
    if (!result) return [];

    if (result.categories && result.series?.length > 1) {
      return decorateChartSeries(result.series, themeColors).map((item, index) => ({
        id: item.id || index,
        label: item.label,
        value: item.data.reduce((sum, value) => sum + value, 0),
        color: item.color || themeColors[index % themeColors.length],
      }));
    }

    if (result.categories && result.series?.[0]?.data) {
      return result.categories.map((label, index) => ({
        id: index,
        label: String(label),
        value: result.series[0].data[index] ?? 0,
        color: themeColors[index % themeColors.length],
      }));
    }

    const points = result.series?.[0]?.data || [];
    return points.map((point, index) => ({
      id: index,
      label: String(point.x ?? point.label ?? index),
      value: point.y ?? point.value ?? 0,
      color: themeColors[index % themeColors.length],
    }));
  }, [result, themeColors]);

  if (!pieData.length) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: height }}>
        <PieChart
          height={height}
          series={[
            {
              data: pieData,
              valueFormatter: valueFormat === 'duration'
                ? (item) => formatDurationSeconds(item.value)
                : undefined,
              innerRadius: compact ? 40 : 0,
              outerRadius: compact ? 110 : 100,
              arcLabel: (item) => formatValue(item.value),
              arcLabelMinAngle: 18,
              paddingAngle: 2,
              cornerRadius: 4,
            },
          ]}
          colors={themeColors}
          legend={{ hidden: true }}
          margin={{ top: 8, bottom: 8, left: 8, right: 8 }}
          sx={{
            '& .MuiChartsAxis-tickLabel': { fill: axis.text },
          }}
        />
      </Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={2}
        justifyContent="flex-start"
        sx={{ px: 1, pt: 0.5 }}
      >
        {pieData.map((item, index) => (
          <Stack key={item.id} direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: item.color || themeColors[index % themeColors.length] || 'primary.main',
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {item.label} · {formatValue(item.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
